import { generatePath, useHistory, useLocation } from 'react-router-dom'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { SuccessfulCopyCallBack } from 'components/CopyModal/components/CopyActions/CopyActions'
import { useModals } from 'almanac-ui/Modal'
import { useCallback } from 'react'
import { HandbooksStore } from 'stores/HandbooksStore'
import { useFinderRefresh } from 'screens/Finder/hooks/useFinderRefresh'
import { FOLDER_DOC_PATH, FOLDER_ROUTE_PATH } from 'paths'
import { useBrand } from 'hooks/useBrand'
import { getArticlePath, getDesignPath } from 'utils/urlUtils'
import { ACTIVITY_FEED_VIEW, RIGHT_RAIL_PARAM } from 'components/DocRightRail/Constants'
import { CopiedItem } from './CopyModal'
import { ArticleFlat, FileType } from 'types'

interface Props {
  title: string
  folderSlug?: string
  handbooksStore?: HandbooksStore
  fileType?: FileType
}

export const useOnItemCopied = ({ title, folderSlug, handbooksStore, fileType = 'article' }: Props) => {
  const history = useHistory()
  const location = useLocation()
  const { closeModal } = useModals()
  const refreshFinder = useFinderRefresh()
  const { defaultFoldersLocation } = useBrand()

  const navigateToCopiedArticle = (
    copiedArticle: Pick<ArticleFlat, 'slug'> | CopiedItem,
    folderSlug: string | null,
  ): [string, string] => {
    const searchParams = new URLSearchParams()
    searchParams.set(RIGHT_RAIL_PARAM, ACTIVITY_FEED_VIEW)
    const pathname = !!folderSlug
      ? generatePath(FOLDER_DOC_PATH, { folderSlug: folderSlug, doc: copiedArticle.slug })
      : getArticlePath({ slug: copiedArticle.slug || '' })
    return [searchParams.toString(), pathname]
  }

  const navigateToCopiedDesign = (copiedDesign: CopiedItem): string => {
    return getDesignPath({ slug: copiedDesign.id || '' })
  }

  const onItemCopied = useCallback<SuccessfulCopyCallBack>(
    (copiedItem, destination) => {
      closeModal()
      const { search: previousUrlSearch, pathname: previousUrlPathname } = location

      showToastMessage(
        `You just copied ${title} to ${destination.selectedFolder?.title || defaultFoldersLocation}`,
        'success',
        {
          onPress: () => {
            history.push({ search: previousUrlSearch, pathname: previousUrlPathname })
          },
          variant: 'proceed',
          label: 'Go to Original',
          type: 'button',
        },
        5,
      )

      if (fileType === 'article') {
        const [search, pathname] = navigateToCopiedArticle(copiedItem, destination.selectedFolder?.seo_string || null)
        history.push({ search, pathname })
      } else if (fileType === 'design') {
        const pathname = navigateToCopiedDesign(copiedItem)
        history.push(pathname)
      } else {
        if (destination.selectedFolder?.seo_string) {
          history.push(generatePath(FOLDER_ROUTE_PATH, { folderSlug: destination.selectedFolder?.seo_string }))
        } else if (copiedItem.handbook?.seo_string) {
          history.push(generatePath(FOLDER_ROUTE_PATH, { folderSlug: copiedItem.handbook?.seo_string }))
        }
      }

      handbooksStore?.refresh(folderSlug)
      handbooksStore?.refresh(destination.selectedFolder?.handbook_key)
      refreshFinder()
    },
    [closeModal, location, title, defaultFoldersLocation, fileType, handbooksStore, folderSlug, refreshFinder, history],
  )

  return {
    onItemCopied,
  }
}
